import React, { useState } from 'react';
import { FaEdit, FaTrashAlt, FaPaperPlane, FaEnvelope, FaComment, FaCheck } from 'react-icons/fa';
import type { Newcomer } from '../../types/newcomer';
import { VISIT_PURPOSE_MAP } from '../../utils/mappings'; // import 추가
import { Modal } from '../common/Modal'; // Modal 컴포넌트 import


interface NewcomerListProps {
  newcomers: Newcomer[];
  onEdit?: (newcomer: Newcomer) => void;
  onDelete?: (id: string) => void;
  onAssign: (id: string) => void; // 담당자 지정을 위한 함수
}

interface MessageTemplate {
  status: 'welcome' | 'orientation' | 'ministry' | 'graduation';
  emailTemplate: string;
  kakaoTemplate: string;
  sendConditions: string[];
}

interface AutoMessageModalProps {
  newcomer: Newcomer;
  isOpen: boolean;
  onClose: () => void;
  onSendMessage: (newcomerId: string, messageType: 'email' | 'kakao', template: string) => void;
}

// 메시지 템플릿 데이터
const MESSAGE_TEMPLATES: Record<string, MessageTemplate> = {
  welcome: {
    status: 'welcome',
    emailTemplate: `부다페스트한인선교교회에 오신 것을 환영합니다!

[새가족 이름] 님,

교회에 처음 방문해 주셔서 진심으로 감사드립니다.
부다페스트한인선교교회는 모든 분들이 하나님의 사랑을 나누고 함께 성장할 수 있는 공동체입니다.

다음 정보를 참고해 주시기 바랍니다:
- 교회 모임: 매주 일요일 오전 11시
- 교회 위치: [교회 주소]
- 연락처: [교회 연락처]

궁금한 점이 있으시면 언제든지 연락 주시기 바랍니다.
하나님의 은혜가 함께하시기를 기도하겠습니다.

감사합니다.
부다페스트한인선교교회 드림`,
    kakaoTemplate: `부다페스트한인선교교회에 오신 것을 환영합니다! [새가족 이름] 님, 교회에 처음 방문해 주셔서 감사합니다. 매주 일요일 오전 11시에 모임이 있습니다. 궁금한 점이 있으면 언제든지 연락 주세요!`,
    sendConditions: ['새가족 등록 시'],
  },
  orientation: {
    status: 'orientation',
    emailTemplate: `새가족 안내 정보

[새가족 이름] 님,

새가족 안내 프로그램에 참여하시게 되어 기쁩니다.
다음은 안내 프로그램 관련 정보입니다:

📅 안내 프로그램 일정:
- 일시: [날짜] [시간]
- 장소: [장소]
- 내용: 교회 소개, 프로그램 안내, Q&A

📋 준비물:
- 개인별 신분증
- 연필과 노트

문의사항 있으시면 연락 주시기 바랍니다.
기대하겠습니다!

부다페스트한인선교교회 드림`,
    kakaoTemplate: `[새가족 이름] 님, 새가족 안내 프로그램에 참여하시게 되어 기쁩니다! [날짜] [시간]에 [장소]에서 진행됩니다. 꼭 참여해 주세요!`,
    sendConditions: ['안내 프로그램 참여 전'],
  },
  ministry: {
    status: 'ministry',
    emailTemplate: `사역 참여 안내

[새가족 이름] 님,

교회 사역에 함께 참여할 수 있게 되어 기쁩니다.
다음은 사역 참여 관련 정보입니다:

🎯 추천 사역:
- 찬양팀
- 어린이 주일학교
- 청년 사역
- 봉사팀

📞 문의:
- 담당자: [담당자 이름]
- 연락처: [연락처]

관심 있는 사역이 있다면 언제든지 연락 주세요.
함께 사역할 수 있기를 기도하겠습니다.

부다페스트한인선교교회 드림`,
    kakaoTemplate: `[새가족 이름] 님, 교회 사역에 함께 참여할 수 있게 되어 기쁩니다! 찬양팀, 어린이 주일학교, 청년 사역, 봉사팀 등 다양한 사역이 있습니다. 관심 있으시면 연락 주세요!`,
    sendConditions: ['사역 참여 의사 표시 시'],
  },
  graduation: {
    status: 'graduation',
    emailTemplate: `새가족 졸업 축하

[새가족 이름] 님,

새가족 프로그램을 무사히 이수하신 것을 진심으로 축하드립니다!
지난 시간 동안 함께한 모든 순간들이 하나님의 은혜가 되었기를 기도합니다.

🎉 졸업을 축하하며:
- 앞으로도 교회 공동체의 일원으로 함께해 주시길 바랍니다
- 새로운 사역에 참여할 기회를 제공해 드리겠습니다
- 언제든지 교회의 도움이 필요하면 연락 주세요

하나님의 복이 가득하시기를 기도합니다.

부다페스트한인선교교회 드림`,
    kakaoTemplate: `[새가족 이름] 님, 새가족 프로그램을 무사히 이수하신 것을 진심으로 축하합니다! 앞으로도 교회 공동체의 일원으로 함께해 주세요. 새로운 사역 기회도 알려드릴게요!`,
    sendConditions: ['새가족 프로그램 이수 시'],
  },
};

// 자동 메시징 모달 컴포넌트
export const AutoMessageModal: React.FC<AutoMessageModalProps> = ({
  newcomer,
  isOpen,
  onClose,
  onSendMessage,
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('welcome');
  const [isSending, setIsSending] = useState(false);

  const template = MESSAGE_TEMPLATES[selectedTemplate];

  const handleSendMessage = async (messageType: 'email' | 'kakao') => {
    setIsSending(true);
    try {
      await onSendMessage(newcomer.id, messageType, template[`${messageType}Template`]);
      onClose();
    } catch (error) {
      console.error('메시지 발송 실패:', error);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`자동 메시지 발송 - ${newcomer.name}`}>
      <div className="space-y-6">
        {/* 새가족 정보 */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
          <h3 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">새가족 정보</h3>
          <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <p><span className="font-medium">이름:</span> {newcomer.name}</p>
            <p><span className="font-medium">연락처:</span> {newcomer.phoneNumber || newcomer.email}</p>
            <p><span className="font-medium">상태:</span> 미지정</p>
          </div>
        </div>

        {/* 메시지 템플릿 선택 */}
        <div>
          <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3">메시지 템플릿 선택</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(MESSAGE_TEMPLATES).map(([key, template]) => (
              <button
                key={key}
                onClick={() => setSelectedTemplate(key)}
                className={`p-3 rounded-lg border text-left transition-colors duration-150 ${
                  selectedTemplate === key
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {template.status === 'welcome' && '환영 메시지'}
                    {template.status === 'orientation' && '안내 프로그램'}
                    {template.status === 'ministry' && '사역 참여'}
                    {template.status === 'graduation' && '졸업 축하'}
                  </span>
                  {selectedTemplate === key && (
                    <FaCheck className="text-blue-500" />
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  발송 조건: {template.sendConditions.join(', ')}
                </p>
              </button>
            ))}
          </div>
        </div>

        {/* 메시지 미리보기 */}
        <div>
          <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-3">메시지 미리보기</h4>
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="mb-3">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">이메일:</span>
              <p className="mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line">
                {template.emailTemplate}
              </p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">카카오톡:</span>
              <p className="mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line">
                {template.kakaoTemplate}
              </p>
            </div>
          </div>
        </div>

        {/* 발송 버튼 */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors duration-150"
          >
            취소
          </button>
          <button
            onClick={() => handleSendMessage('email')}
            disabled={isSending}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors duration-150"
          >
            <FaEnvelope className="mr-2" />
            {isSending ? '발송 중...' : '이메일 발송'}
          </button>
          <button
            onClick={() => handleSendMessage('kakao')}
            disabled={isSending}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors duration-150"
          >
            <FaComment className="mr-2" />
            {isSending ? '발송 중...' : '카카오톡 발송'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export const NewcomerList: React.FC<NewcomerListProps> = ({ newcomers, onEdit, onDelete, onAssign }) => {
  const [selectedNewcomer, setSelectedNewcomer] = useState<Newcomer | null>(null);
  const [isMessageModalOpen, setIsMessageModalOpen] = useState(false);

  // 자동 메시지 발송 핸들러
  const handleSendMessage = async (newcomerId: string, messageType: 'email' | 'kakao', template: string) => {
    // 실제 메시지 발송 로직 구현
    console.log(`${messageType} 메시지 발송:`, { newcomerId, template });
    
    // 여기에 실제 이메일/카카오톡 API 호출 로직이 들어감
    // 예: await sendEmail(newcomer.email, template);
    // 예: await sendKakaoMessage(newcomer.kakaoId, template);
  };

  // 메시지 발송 모달 열기
  const openMessageModal = (newcomer: Newcomer) => {
    setSelectedNewcomer(newcomer);
    setIsMessageModalOpen(true);
  };

  // 메시지 발송 모달 닫기
  const closeMessageModal = () => {
    setIsMessageModalOpen(false);
    setSelectedNewcomer(null);
  };

  if (newcomers.length === 0) {
    return (
      <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md text-center text-gray-500 dark:text-gray-400">
        등록된 새가족이 없습니다.
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
      {/* Desktop Table */}
      <div className="overflow-x-auto hidden md:block">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-center text-base font-medium text-gray-600 dark:text-gray-300 uppercase">번호</th>
              <th className="px-6 py-3 text-center text-base font-medium text-gray-600 dark:text-gray-300 uppercase">이름</th>
              <th className="px-6 py-3 text-center text-base font-medium text-gray-600 dark:text-gray-300 uppercase">성별</th>
              <th className="px-6 py-3 text-center text-base font-medium text-gray-600 dark:text-gray-300 uppercase">연락처</th>
              <th className="px-6 py-3 text-center text-base font-medium text-gray-600 dark:text-gray-300 uppercase">방문목적</th>
              <th className="px-6 py-3 text-center text-base font-medium text-gray-600 dark:text-gray-300 uppercase">인도자</th>
              <th className="px-6 py-3 text-center text-base font-medium text-gray-600 dark:text-gray-300 uppercase">담당자</th>
              <th className="px-6 py-3 text-center text-base font-medium text-gray-600 dark:text-gray-300 uppercase">관리</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {newcomers.map((newcomer) => (
              <tr key={newcomer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400">{newcomer.customId}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center text-gray-900 dark:text-gray-200">{newcomer.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400">-</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400">{newcomer.phoneNumber || newcomer.email}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400">{newcomer.visitPurpose === 'other' ? newcomer.visitPurposeDetail : (VISIT_PURPOSE_MAP[newcomer.visitPurpose] || newcomer.visitPurpose)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400">{newcomer.guide}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-600 dark:text-gray-400">{newcomer.personInCharge || <button onClick={() => onAssign(newcomer.id)} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">미지정</button>}</td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                  <div className="flex items-center justify-center space-x-3">
                    <button onClick={() => openMessageModal(newcomer)} className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 transition-colors duration-150" aria-label={`Send message to ${newcomer.name}`}><FaPaperPlane /></button>
                    {onEdit && (
                      <button onClick={() => onEdit(newcomer)} className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 transition-colors duration-150" aria-label={`Edit ${newcomer.name}'s record`}><FaEdit /></button>
                    )}
                    {onDelete && (
                      <button onClick={() => onDelete(newcomer.id)} className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 transition-colors duration-150" aria-label={`Delete ${newcomer.name}'s record`}><FaTrashAlt /></button>
                    )}
                    {!onEdit && !onDelete && (
                      <span className="text-gray-500 text-xs">읽기 전용</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden">
        {newcomers.map((newcomer) => (
          <div key={newcomer.id} className="border-b border-gray-200 dark:border-gray-700 p-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{newcomer.name}</h3>
              <div className="flex gap-x-3">
                <button onClick={() => openMessageModal(newcomer)} className="text-green-600 dark:text-green-400" aria-label={`Send message to ${newcomer.name}`}><FaPaperPlane size={18} /></button>
                {onEdit && (
                  <button onClick={() => onEdit(newcomer)} className="text-indigo-600 dark:text-indigo-400" aria-label={`Edit ${newcomer.name}'s record`}><FaEdit size={18} /></button>
                )}
                {onDelete && (
                  <button onClick={() => onDelete(newcomer.id)} className="text-red-600 dark:text-red-400" aria-label={`Delete ${newcomer.name}'s record`}><FaTrashAlt size={18} /></button>
                )}
                {!onEdit && !onDelete && (
                  <div className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                    <span className="text-gray-500 text-xs">읽기 전용</span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <p><span className="font-semibold">번호:</span> {newcomer.customId}</p>
              <p><span className="font-semibold">연락처:</span> {newcomer.phoneNumber || newcomer.email}</p>
              <p><span className="font-semibold">방문목적:</span> {newcomer.visitPurpose === 'other' ? newcomer.visitPurposeDetail : (VISIT_PURPOSE_MAP[newcomer.visitPurpose] || newcomer.visitPurpose)}</p>
              <p><span className="font-semibold">인도자:</span> {newcomer.guide}</p>
              <p><span className="font-semibold">담당자:</span> {newcomer.personInCharge || <button onClick={() => onAssign(newcomer.id)} className="text-xs text-blue-500 hover:underline">지정하기</button>}</p>
            </div>
          </div>
        ))}
      </div>
      
      {/* 자동 메시징 모달 */}
      {selectedNewcomer && (
        <AutoMessageModal
          newcomer={selectedNewcomer}
          isOpen={isMessageModalOpen}
          onClose={closeMessageModal}
          onSendMessage={handleSendMessage}
        />
      )}
    </div>
  );
};
